/**
 * Main hook for ImageDirectorChat page
 *
 * Mirrors the useImages.ts pattern: all state, handlers, effects, and
 * derived values live here – the component only does layout.
 *
 * Gallery logic (lightbox, keyboard nav, download/share/animate/delete)
 * lives in useDirectorGallery.ts.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { captureEvent } from "@/lib/analyticsQueue";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "@/lib/toast";
import { useModels } from "@/hooks/useModels";
import { useModelWarning } from "@/hooks/useModelWarning";
import { apiPost } from "@/lib/api/apiClient";
import { uploadSingleImageToR2 } from "@/lib/api/uploadImage";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { captureGenerationError } from "@/lib/sentry";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { useConversationPersistence } from "./useConversationPersistence";
import { useDirectorGallery } from "./useDirectorGallery";
import type { ChatMessage, ChatReferenceImage, Resolution, AspectRatio } from "./types";
import { SCROLL_THRESHOLD } from "./types";
import { CONTENT_FLAGGED_CODE } from "@shared/schemas/enums";
import { ASPECT_RATIOS, RESOLUTIONS } from "@/components/flowly/AspectRatioPicker/constants";

const FLOW_STATE_KEY = FLOW_KEYS.IMAGE_DIRECTOR_CHAT;

export function useImageDirectorChat() {
  const { user, profile } = useAuth();
  const { deductCredits, refreshCredits, handleCreditError } = useCredits();
  const { getDirectorCost, getImageModelCost, costsLoading } = useCreditCosts();
  const { t, language, locale } = useLanguage();
  const { models, refetchModels } = useModels("image");
  const [imageModel, setImageModel] = useState<string>("");
  const modelWarning = useModelWarning(imageModel, models);

  useEffect(() => {
    if (!imageModel && models.length > 0) {
      const defaultModel = models.find((m) => m.isDefault) ?? models[0];
      setImageModel(defaultModel.id);
    }
  }, [models, imageModel]);

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.IMAGE_DIRECTOR });
  }, []);

  const selectedModel = models.find((m) => m.id === imageModel);

  const availableAspectRatios = useMemo(() => {
    const supported = selectedModel?.capabilities.supportedAspectRatios;
    if (!supported) return ASPECT_RATIOS;
    return ASPECT_RATIOS.filter((r) => supported.includes(r.id));
  }, [selectedModel]);

  const availableResolutions = useMemo(() => {
    const supported = selectedModel?.capabilities.supportedResolutions;
    if (!supported) return RESOLUTIONS;
    return RESOLUTIONS.filter((r) => supported.includes(r.id));
  }, [selectedModel]);

  const handleImageModelChange = (modelId: string) => {
    setImageModel(modelId);
    const newModel = models.find((m) => m.id === modelId);
    const supported = newModel?.capabilities.supportedAspectRatios;
    if (supported && !supported.includes(aspectRatio)) {
      setAspectRatio((supported[0] ?? "1:1") as AspectRatio);
    }
    const supportedRes = newModel?.capabilities.supportedResolutions;
    if (supportedRes && !supportedRes.includes(resolution)) {
      setResolution(supportedRes[0] as Resolution);
    }
  };

  // ── URL params (conversation restore) ───────

  const [searchParams, setSearchParams] = useSearchParams();
  const urlConversationId = searchParams.get("c");

  // ── Gallery (delegated hook) ────────────────

  const gallery = useDirectorGallery();

  // ── Refs ──────────────────────────────────────

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // ── Persistence ───────────────────────────────

  const {
    conversationId,
    createConversation,
    saveMessage,
    updateTitle,
    loadConversation,
    resetConversation,
  } = useConversationPersistence();

  // ── Chat state ────────────────────────────────

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isReadyToGenerate, setIsReadyToGenerate] = useState(false);

  // Reference mode
  const [useReferenceImages, setUseReferenceImages] = useState(false);
  const [referenceImages, setReferenceImages] = useState<ChatReferenceImage[]>([]);

  // ── Generation options ────────────────────────

  const [quantity, setQuantity] = useState(2);
  const [resolution, setResolution] = useState<Resolution>("2k");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("1:1");
  const { pendingCount, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({ pipeline: "director", dbPendingCount: gallery.directorPendingCount, isDbLoading: gallery.directorLoading });
  const [generatingCount, setGeneratingCount] = useState(0);
  const generating = generatingCount > 0;

  // ── Scroll state ──────────────────────────────

  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);

  // ── History state ─────────────────────────────

  const [showHistory, setShowHistory] = useState(false);
  const [viewingHistoryId, setViewingHistoryId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  // ── Derived values ────────────────────────────

  const getCreditCost = () => {
    return getDirectorCost(resolution, imageModel) * quantity;
  };

  // ── Scroll helpers ────────────────────────────

  const checkIfNearBottom = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior });
    }
    setShowScrollButton(false);
    setNewMessagesCount(0);
  }, []);

  const handleScroll = useCallback(() => {
    const nearBottom = checkIfNearBottom();
    setIsNearBottom(nearBottom);
    if (nearBottom) {
      setShowScrollButton(false);
      setNewMessagesCount(0);
    }
  }, [checkIfNearBottom]);

  // ── Effects ───────────────────────────────────

  // Restore conversation from URL param (?c=<id>) on mount
  const hasAttemptedRestore = useRef(false);

  useEffect(() => {
    if (hasAttemptedRestore.current) return;
    hasAttemptedRestore.current = true;

    if (!user || !urlConversationId) {
      setIsRestoring(false);
      return;
    }

    (async () => {
      try {
        const loadedMessages = await loadConversation(urlConversationId);
        if (loadedMessages.length > 0) {
          setMessages(loadedMessages.map(m => ({
            role: m.role,
            content: m.content,
            imageUrl: m.imageUrl,
          })));

          const hasPrompt = loadedMessages.some(m =>
            m.content.includes("[MAIN SUBJECT & SCENE]")
          );
          setIsReadyToGenerate(hasPrompt);
        }
      } catch (err) {
        console.error("Error restoring conversation:", err);
        captureGenerationError(err, {
          flow: FLOW_IDS.IMAGE_DIRECTOR,
          action: "restore_conversation",
          endpoint: "supabase:agent_conversations",
        });
      } finally {
        setIsRestoring(false);
      }
    })();
  }, [user, urlConversationId, loadConversation]);

  const setUrlConversationId = useCallback((id: string) => {
    setSearchParams({ c: id }, { replace: true });
  }, [setSearchParams]);

  // Send initial greeting (new conversations only, local-only)
  useEffect(() => {
    if (messages.length === 0 && !viewingHistoryId && !isRestoring) {
      const greeting = t.director.greeting;
      setMessages([{ role: "assistant", content: greeting }]);
    }
  }, [language, viewingHistoryId, isRestoring, t.director.greeting, messages.length]);

  // Auto-scroll on new messages (internal container scroll only)
  useEffect(() => {
    if (messages.length === 0) return;
    if (isNearBottom) {
      scrollToBottom("smooth");
    } else {
      setShowScrollButton(true);
      setNewMessagesCount(prev => prev + 1);
    }
  }, [messages.length, isNearBottom, scrollToBottom]);

  // ── Chat handlers ─────────────────────────────

  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: "user", content: inputValue.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    // Save to DB only if conversation already exists (resumed from history)
    if (conversationId) {
      await saveMessage("user", userMessage.content);
      if (messages.filter(m => m.role === "user").length === 0) {
        const title = userMessage.content.slice(0, 50) + (userMessage.content.length > 50 ? "..." : "");
        await updateTitle(title);
      }
    }

    try {
      const data = await apiPost<{ success: boolean; message?: string; promptReady?: boolean }>("/api/image-director", {
        messages: [...messages, userMessage],
      });

      if (data?.success && data?.message) {
        const assistantMessage: ChatMessage = { role: "assistant", content: data.message };
        setMessages(prev => [...prev, assistantMessage]);
        if (conversationId) {
          await saveMessage("assistant", assistantMessage.content);
        }

        // Backend tells us when the prompt is finalized
        if (data.promptReady) {
          setIsReadyToGenerate(true);

          // Create conversation and bulk-save all messages when prompt is finalized
          if (!conversationId && user) {
            const firstUserMsg = [...messages, userMessage].find(m => m.role === "user");
            const title = firstUserMsg
              ? firstUserMsg.content.slice(0, 50) + (firstUserMsg.content.length > 50 ? "..." : "")
              : t.director.defaultTitle;
            const newId = await createConversation(title);
            if (newId) {
              setUrlConversationId(newId);
              const allMessages = [...messages, userMessage, assistantMessage];
              for (const msg of allMessages) {
                await saveMessage(msg.role, msg.content, undefined, newId);
              }
            }
          }
        }

        // Check for restart indicators
        const lowerMessage = data.message.toLowerCase();
        const restartIndicators = ["נתחיל מחדש", "start fresh", "start over", "let's start"];
        if (restartIndicators.some(i => lowerMessage.includes(i.toLowerCase()))) {
          setIsReadyToGenerate(false);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.IMAGE_DIRECTOR,
        action: "send_message",
        endpoint: "/api/image-director",
      });
      if (!(error as Error & { isRateLimited?: boolean }).isRateLimited) {
        const errorMessage = error instanceof Error ? error.message : undefined;
        toast.error(t.director.error, {
          description: errorMessage || t.director.aiCommError,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, messages, isLoading, saveMessage, conversationId, user, createConversation, updateTitle, setUrlConversationId, t.director.error, t.director.aiCommError, t.director.defaultTitle]);

  // ── Reference image handlers ──────────────────

  const handleToggleReference = () => {
    setUseReferenceImages(prev => !prev);
  };

  const handleImagesChange = (images: ChatReferenceImage[]) => {
    const prevLength = referenceImages.length;
    setReferenceImages(images);

    if (images.length > prevLength) {
      captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, { flow: FLOW_IDS.IMAGE_DIRECTOR });
    }

    if (images.length > 0 && !useReferenceImages) {
      setUseReferenceImages(true);
    }

    if (images.length === 0) {
      setUseReferenceImages(false);
    }

    if (images.length > prevLength) {
      const ackMessage: ChatMessage = { role: "assistant", content: t.director.referenceAck };
      setMessages(prev => [...prev, ackMessage]);
      if (conversationId) {
        saveMessage("assistant", ackMessage.content);
      }
    }
  };

  // ── Generation handler ────────────────────────

  const generateImages = async () => {
    if (messages.length < 3) {
      toast.info(t.director.oneMoreStep, {
        description: t.director.oneMoreStepDesc,
      });
      return;
    }

    const plan = profile?.subscription_name;
    if (generatingCount > 0 && plan !== "pro" && plan !== "creator") {
      toast.info(t.generate.generationInProgress, { description: t.generate.parallelProRequired });
      return;
    }

    const maxConcurrent = 4;
    if (generatingCount >= maxConcurrent) {
      toast.info(t.director.generationInProgress, {
        description: t.director.maxConcurrent,
      });
      return;
    }

    const cost = getCreditCost();
    if (!deductCredits(cost)) return;

    incrementPending(quantity);
    setGeneratingCount(prev => prev + 1);

    (async () => {
      try {
        // Upload reference images to R2, then pass URLs
        let referenceImageUrls: string[] = [];
        if (useReferenceImages && referenceImages.length > 0) {
          referenceImageUrls = await Promise.all(
            referenceImages.map(async (img) => {
              // If it's already a URL (from R2/CDN), use directly; otherwise upload base64
              if (img.url.startsWith("https://")) return img.url;
              return uploadSingleImageToR2(img.base64);
            })
          );
        }

        const data = await apiPost<{ success: boolean; images?: string[] }>("/api/generate-from-director", {
          messages,
          quantity,
          resolution,
          aspectRatio,
          referenceImageUrls,
          modelId: imageModel,
        });

        if (data?.success && data?.images?.length > 0) {
          gallery.refetchDirectorImages();
          clearFlowState(FLOW_STATE_KEY);

          toast.success(t.director.imagesCreated, {
            description: t.director.imagesCreatedDesc.replace("{count}", String(data.images.length)),
          });

          await refreshCredits();
        }
      } catch (error) {
        console.error("Error generating images:", error);
        captureGenerationError(error, {
          flow: FLOW_IDS.IMAGE_DIRECTOR,
          action: "generate",
          model: imageModel,
          endpoint: "/api/generate-from-director",
        });
        refetchModels();
        if (!handleCreditError(error)) {
          if (error instanceof Error && (error as Error & { code?: string }).code === CONTENT_FLAGGED_CODE) {
            toast.error(t.generate.contentFlagged, {
              description: t.generate.contentFlaggedDesc,
            });
          } else {
            const errorMessage = error instanceof Error ? error.message : undefined;
            toast.error(t.director.generationFailed, {
              description: errorMessage || t.director.generationFailedWithRefund,
            });
          }
        }
        await refreshCredits();
      } finally {
        decrementPending(quantity);
        setGeneratingCount(prev => Math.max(0, prev - 1));
      }
    })();
  };

  // Auth gate lives inside GenerateButton, so this is just a rename.
  const handleGenerate = generateImages;

  // Persist chat history + active settings + reference images so a hop
  // through the signup modal (and Google OAuth roundtrip) doesn't wipe the
  // conversation. `referenceImages` is an image key → its (multi-MB) base64
  // bytes go to IndexedDB, NOT localStorage — otherwise one large reference
  // image blows the ~5 MB quota and the whole record (conversation included)
  // silently fails to persist.
  useFlowPersistence({
    key: FLOW_STATE_KEY,
    state: {
      messages,
      inputValue,
      isReadyToGenerate,
      useReferenceImages,
      referenceImages,
      quantity,
      resolution,
      aspectRatio,
      imageModel,
    },
    imageKeys: ["referenceImages"],
    // Only persist when the user has actually started a conversation; saves
    // storage churn while they sit idle on the empty page.
    enabled: messages.length > 0,
    onRestore: (s) => {
      if (Array.isArray(s.messages)) setMessages(s.messages as ChatMessage[]);
      if (typeof s.inputValue === "string") setInputValue(s.inputValue);
      if (typeof s.isReadyToGenerate === "boolean") setIsReadyToGenerate(s.isReadyToGenerate);
      if (typeof s.useReferenceImages === "boolean") setUseReferenceImages(s.useReferenceImages);
      if (Array.isArray(s.referenceImages)) setReferenceImages(s.referenceImages as ChatReferenceImage[]);
      if (typeof s.quantity === "number") setQuantity(s.quantity);
      if (s.resolution === "standard" || s.resolution === "2k" || s.resolution === "4k") {
        setResolution(s.resolution);
      }
      if (typeof s.aspectRatio === "string") {
        setAspectRatio(s.aspectRatio as AspectRatio);
      }
      if (typeof s.imageModel === "string") setImageModel(s.imageModel);
    },
  });

  const handleDisabledClick = useCallback(() => {
    if (viewingHistoryId) return;
    document.querySelector('[data-scroll-target="chat-input"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [viewingHistoryId]);

  // ── Regenerate as reference ──────────────────

  const handleRegenerateAsReference = useCallback(async () => {
    if (!gallery.selectedImage) return;
    const imageUrl = gallery.selectedImage.url;
    gallery.setSelectedImage(null);

    try {
      const response = await fetch(imageUrl, { mode: "cors", cache: "no-store" });
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        const newRef: ChatReferenceImage = {
          id: crypto.randomUUID(),
          url: imageUrl,
          base64,
        };
        handleImagesChange([...referenceImages, newRef]);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("Error adding image as reference:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.IMAGE_DIRECTOR,
        action: "regenerate_as_reference",
        endpoint: "r2:fetch-image",
      });
      toast.error(t.common.error);
    }
  }, [gallery, handleImagesChange, referenceImages, t.common.error]);

  // ── Conversation handlers ─────────────────────

  const handleSelectConversation = async (selectedConversationId: string) => {
    setViewingHistoryId(selectedConversationId);
    setShowHistory(false);

    const loadedMessages = await loadConversation(selectedConversationId);
    if (loadedMessages.length > 0) {
      setMessages(loadedMessages.map(m => ({
        role: m.role,
        content: m.content,
        imageUrl: m.imageUrl,
      })));

      const hasPrompt = loadedMessages.some(m =>
        m.content.includes("[MAIN SUBJECT & SCENE]")
      );
      setIsReadyToGenerate(hasPrompt);
    }
  };

  const handleResumeConversation = async (selectedConversationId: string) => {
    const loadedMessages = await loadConversation(selectedConversationId);
    if (loadedMessages.length > 0) {
      setMessages(loadedMessages.map(m => ({
        role: m.role,
        content: m.content,
        imageUrl: m.imageUrl,
      })));

      const hasPrompt = loadedMessages.some(m =>
        m.content.includes("[MAIN SUBJECT & SCENE]")
      );
      setIsReadyToGenerate(hasPrompt);
    }

    setUrlConversationId(selectedConversationId);
    setViewingHistoryId(null);
    setShowHistory(false);
  };

  const startNewConversation = () => {
    setSearchParams({}, { replace: true });
    setViewingHistoryId(null);
    setMessages([]);
    setIsReadyToGenerate(false);
    resetConversation();
  };

  // ── Return ────────────────────────────────────

  return {
    // Refs
    messagesEndRef,
    chatContainerRef,

    // Auth
    locale,

    // Chat
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isReadyToGenerate,
    sendMessage,

    // Reference images
    useReferenceImages,
    referenceImages,
    handleToggleReference,
    handleImagesChange,

    // Generation
    generating,
    quantity,
    setQuantity,
    resolution,
    availableResolutions,
    setResolution,
    aspectRatio,
    setAspectRatio,
    pendingCount,
    directorPendingImages: gallery.directorPendingImages,
    directorCompletingIds: gallery.directorCompletingIds,
    handleGenerate,
    getCreditCost,
    getDirectorCost,
    getImageModelCost,
    costsLoading,

    // Scroll
    showScrollButton,
    newMessagesCount,
    handleScroll,
    scrollToBottom,

    // History
    showHistory,
    setShowHistory,
    viewingHistoryId,
    handleSelectConversation,
    handleResumeConversation,
    startNewConversation,

    // Models
    models,
    modelWarning,
    imageModel,
    handleImageModelChange,
    availableAspectRatios,

    // Disabled click
    handleDisabledClick,

    // Gallery (spread from sub-hook)
    ...gallery,
    handleRegenerateAsReference,
  };
}
